/**
 * Screenshot Data Generator
 * Generates realistic-looking fake data for app store screenshots
 * Only works for the special demo account (@aaron)
 */

import { format, eachDayOfInterval, parseISO, getDay, differenceInDays } from "date-fns";

// The demo account user ID and username
export const DEMO_ACCOUNT_USER_ID = "12dc3318-6c81-4a2c-970c-5501c2f58d14";
export const DEMO_ACCOUNT_USERNAME = "aaron";
// Also support old usernames for backwards compatibility
export const DEMO_ACCOUNT_USERNAMES = ["mytestact", "testact"];

// Logging archetypes that simulate different user behaviors
export type LoggingArchetype = 
  | 'dedicated'      // Logs every day, usually hits goals
  | 'weekday_warrior' // Only logs Mon-Fri, weekends off
  | 'weekend_only'   // Only logs weekends
  | 'sporadic'       // Logs randomly, 40-60% of days
  | 'streak_builder' // Builds up streaks then falls off
  | 'new_year'       // Strong start, tapers off
  | 'gradual_improve' // Gets better over time
  | 'seasonal'       // More active in certain months
  | 'perfectionist'  // Almost always hits exactly at goal
  | 'overachiever';  // Often exceeds goals

// Get a random archetype, weighted so over-goal archetypes are less common
export function getRandomArchetype(): LoggingArchetype {
  // Weighted pool: moderate archetypes appear more often,
  // perfectionist/overachiever are rare
  const weightedArchetypes: LoggingArchetype[] = [
    'dedicated',
    'weekday_warrior',
    'weekday_warrior',
    'weekend_only',
    'sporadic',
    'sporadic',
    'streak_builder',
    'streak_builder',
    'new_year',
    'gradual_improve',
    'gradual_improve',
    'seasonal',
    'seasonal',
    'perfectionist',   // 1x weight (rare)
    'overachiever',    // 1x weight (rare)
  ];
  return weightedArchetypes[Math.floor(Math.random() * weightedArchetypes.length)];
}

interface MetricConfig {
  key: string;
  goal: number;
  isInverse?: boolean;
  isBoolean?: boolean;
  minValue?: number;
  maxValue?: number;
  unit?: string;
}

interface GeneratedEntry {
  entry_date: string;
  value: number | boolean | null;
}

// Seeded random for reproducibility within a session
function seededRandom(seed: number): () => number {
  return function() {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
}

// Generate a value based on archetype behavior for a specific date
function generateValueForDate(
  date: Date,
  config: MetricConfig,
  archetype: LoggingArchetype,
  startDate: Date,
  endDate: Date,
  random: () => number
): number | boolean | null {
  const dayOfWeek = getDay(date); // 0 = Sunday, 6 = Saturday
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  const totalDays = differenceInDays(endDate, startDate);
  const currentDay = differenceInDays(date, startDate);
  const progress = totalDays > 0 ? currentDay / totalDays : 0;
  
  const { goal, isInverse, isBoolean, minValue = 0, maxValue } = config;
  const effectiveMax = maxValue || goal * 1.5;

  // Determine if this day should have an entry based on archetype
  let shouldLog = true;
  let goalMultiplier = 1; // How close to goal (1 = exactly at goal)
  let variance = 0.2; // How much variance around the target

  switch (archetype) {
    case 'dedicated':
      shouldLog = random() > 0.05; // 95% logging rate
      goalMultiplier = 0.7 + random() * 0.25; // 70-95% of goal
      variance = 0.12;
      break;

    case 'weekday_warrior':
      shouldLog = !isWeekend && random() > 0.1;
      goalMultiplier = 0.55 + random() * 0.35; // 55-90% of goal
      break;

    case 'weekend_only':
      shouldLog = isWeekend && random() > 0.15;
      goalMultiplier = 0.5 + random() * 0.4; // 50-90% of goal
      break;

    case 'sporadic':
      shouldLog = random() > 0.45; // ~55% logging rate
      goalMultiplier = 0.25 + random() * 0.55; // 25-80% of goal
      variance = 0.3;
      break;

    case 'streak_builder':
      // Build streaks of 5-15 days, then miss 2-5 days
      const cycleLength = 20;
      const posInCycle = currentDay % cycleLength;
      shouldLog = posInCycle < 12 + Math.floor(random() * 5);
      goalMultiplier = posInCycle < 7 ? 0.4 + posInCycle * 0.07 : 0.75 + random() * 0.15; // peak 75-90%
      break;

    case 'new_year':
      // Strong in Jan-Feb, tapers off
      const month = date.getMonth();
      if (month <= 1) {
        shouldLog = random() > 0.1;
        goalMultiplier = 0.75 + random() * 0.2; // 75-95% at best
      } else if (month <= 3) {
        shouldLog = random() > 0.3;
        goalMultiplier = 0.55 + random() * 0.25; // 55-80%
      } else {
        shouldLog = random() > 0.5;
        goalMultiplier = 0.3 + random() * 0.35; // 30-65%
      }
      break;

    case 'gradual_improve':
      shouldLog = random() > (0.4 - progress * 0.3); // Gets more consistent
      goalMultiplier = 0.35 + progress * 0.45 + random() * 0.15; // tops out ~80-95%
      break;

    case 'seasonal':
      // More active in spring/summer (months 3-8)
      const seasonMonth = date.getMonth();
      const isActiveSeason = seasonMonth >= 3 && seasonMonth <= 8;
      shouldLog = isActiveSeason ? random() > 0.15 : random() > 0.5;
      goalMultiplier = isActiveSeason ? 0.65 + random() * 0.25 : 0.3 + random() * 0.35; // active: 65-90%, off: 30-65%
      break;

    case 'perfectionist':
      shouldLog = random() > 0.08;
      goalMultiplier = 0.88 + random() * 0.12; // 88-100%, occasionally just hits goal
      variance = 0.05;
      break;

    case 'overachiever':
      shouldLog = random() > 0.1;
      goalMultiplier = 0.85 + random() * 0.3; // 85-115%, sometimes exceeds
      variance = 0.15;
      break;
  }

  if (!shouldLog) {
    return null;
  }

  if (isBoolean) {
    // For boolean metrics (habits), use probability based on goal multiplier
    // Bad habits (isInverse) should be logged less frequently when doing well
    if (isInverse) {
      return random() < (1 - goalMultiplier * 0.7);
    }
    return random() < goalMultiplier * 0.8;
  }

  // Calculate numeric value
  let targetValue = goal * goalMultiplier;
  const varianceAmount = goal * variance;
  targetValue += (random() - 0.5) * 2 * varianceAmount;

  if (isInverse) {
    // For inverse metrics (calories, added sugars): being UNDER goal is good (green),
    // being OVER goal is bad (red). Most days should land safely under goal.
    // Cap the base value to ~92% of goal so variance doesn't accidentally push over.
    if (targetValue > goal * 0.92) {
      targetValue = goal * (0.78 + random() * 0.14); // pull back to 78-92% of goal
    }
    // ~10% chance of a "slip up" day that goes slightly over goal
    if (random() > 0.90) {
      targetValue = goal * (1.02 + random() * 0.13); // 102-115% of goal
    }
  }

  // Clamp to reasonable bounds
  targetValue = Math.max(minValue, Math.min(effectiveMax, targetValue));

  // Round based on typical precision for the metric
  if (goal >= 100) {
    return Math.round(targetValue);
  } else if (goal >= 10) {
    return Math.round(targetValue * 10) / 10;
  } else {
    return Math.round(targetValue * 100) / 100;
  }
}

// Generate random data for a metric
export function generateRandomMetricData(
  metricConfig: MetricConfig,
  startDate: Date = new Date('2025-03-01'),
  endDate: Date = new Date('2026-04-30')
): GeneratedEntry[] {
  const archetype = getRandomArchetype();
  const seed = Date.now() + Math.random() * 1000000;
  const random = seededRandom(seed);

  const days = eachDayOfInterval({ start: startDate, end: endDate });
  
  return days.map(date => ({
    entry_date: format(date, 'yyyy-MM-dd'),
    value: generateValueForDate(date, metricConfig, archetype, startDate, endDate, random)
  }));
}

// Generate intensity values (0-4, with negatives for inverse metrics going over)
export function generateRandomIntensityData(
  metricConfig: MetricConfig,
  startDate: Date = new Date('2025-03-01'),
  endDate: Date = new Date('2026-04-30')
): Map<string, number> {
  const entries = generateRandomMetricData(metricConfig, startDate, endDate);
  const intensityMap = new Map<string, number>();

  entries.forEach(entry => {
    if (entry.value === null) {
      intensityMap.set(entry.entry_date, 0);
    } else if (typeof entry.value === 'boolean') {
      // For inverse booleans (yes = bad), true should be red (-4)
      if (metricConfig.isInverse) {
        intensityMap.set(entry.entry_date, entry.value ? -4 : 0);
      } else {
        intensityMap.set(entry.entry_date, entry.value ? 4 : 0);
      }
    } else {
      const { goal, isInverse } = metricConfig;
      const value = entry.value as number;
      const percentage = (value / goal) * 100;

      if (isInverse) {
        // For inverse metrics, being over goal is bad (negative intensity = red)
        if (percentage > 100) {
          const overshoot = percentage - 100;
          if (overshoot >= 50) intensityMap.set(entry.entry_date, -4);
          else if (overshoot >= 25) intensityMap.set(entry.entry_date, -3);
          else if (overshoot >= 10) intensityMap.set(entry.entry_date, -2);
          else intensityMap.set(entry.entry_date, -1);
        } else {
          if (percentage >= 90) intensityMap.set(entry.entry_date, 4);
          else if (percentage >= 75) intensityMap.set(entry.entry_date, 3);
          else if (percentage >= 50) intensityMap.set(entry.entry_date, 2);
          else if (percentage > 0) intensityMap.set(entry.entry_date, 1);
          else intensityMap.set(entry.entry_date, 0);
        }
      } else {
        // Normal metrics
        if (percentage >= 100) intensityMap.set(entry.entry_date, 4);
        else if (percentage >= 75) intensityMap.set(entry.entry_date, 3);
        else if (percentage >= 50) intensityMap.set(entry.entry_date, 2);
        else if (percentage > 0) intensityMap.set(entry.entry_date, 1);
        else intensityMap.set(entry.entry_date, 0);
      }
    }
  });

  return intensityMap;
}

// Generate both intensity and raw value data for a metric
export function generateRandomDataWithValues(
  metricConfig: MetricConfig,
  startDate: Date = new Date('2025-03-01'),
  endDate: Date = new Date('2026-04-30')
): { intensities: Map<string, number>; values: Map<string, number | boolean | null> } {
  const entries = generateRandomMetricData(metricConfig, startDate, endDate);
  const intensityMap = new Map<string, number>();
  const valueMap = new Map<string, number | boolean | null>();

  entries.forEach(entry => {
    // Store the raw value
    valueMap.set(entry.entry_date, entry.value);
    
    if (entry.value === null) {
      intensityMap.set(entry.entry_date, 0);
    } else if (typeof entry.value === 'boolean') {
      // For inverse booleans (yes = bad), true should be red (-4)
      if (metricConfig.isInverse) {
        intensityMap.set(entry.entry_date, entry.value ? -4 : 0);
      } else {
        intensityMap.set(entry.entry_date, entry.value ? 4 : 0);
      }
    } else {
      const { goal, isInverse } = metricConfig;
      const value = entry.value as number;
      const percentage = (value / goal) * 100;

      if (isInverse) {
        if (percentage > 100) {
          const overshoot = percentage - 100;
          if (overshoot >= 50) intensityMap.set(entry.entry_date, -4);
          else if (overshoot >= 25) intensityMap.set(entry.entry_date, -3);
          else if (overshoot >= 10) intensityMap.set(entry.entry_date, -2);
          else intensityMap.set(entry.entry_date, -1);
        } else {
          if (percentage >= 90) intensityMap.set(entry.entry_date, 4);
          else if (percentage >= 75) intensityMap.set(entry.entry_date, 3);
          else if (percentage >= 50) intensityMap.set(entry.entry_date, 2);
          else if (percentage > 0) intensityMap.set(entry.entry_date, 1);
          else intensityMap.set(entry.entry_date, 0);
        }
      } else {
        if (percentage >= 100) intensityMap.set(entry.entry_date, 4);
        else if (percentage >= 75) intensityMap.set(entry.entry_date, 3);
        else if (percentage >= 50) intensityMap.set(entry.entry_date, 2);
        else if (percentage > 0) intensityMap.set(entry.entry_date, 1);
        else intensityMap.set(entry.entry_date, 0);
      }
    }
  });

  return { intensities: intensityMap, values: valueMap };
}

// Check if the current user is the demo account
export function isDemoAccount(username: string | undefined): boolean {
  if (!username) return false;
  const lowerUsername = username.toLowerCase();
  return lowerUsername === DEMO_ACCOUNT_USERNAME.toLowerCase() || 
         DEMO_ACCOUNT_USERNAMES.some(u => u.toLowerCase() === lowerUsername);
}
